/**
 * Google Identity Services (GSI), used directly.
 *
 * This replaces `react-google-one-tap-login@0.1.1`, which was the reason
 * Google sign-in could not be secured (OOH-48/OOH-49). That library took the
 * raw ID token Google returns as `credential`, called
 * `https://oauth2.googleapis.com/tokeninfo` with it *itself*, and handed the
 * app only the decoded claims (`dist/index.es5.js:97-123`). The raw JWT never
 * reached application code, so the SPA had nothing signed to send and the API
 * had nothing to verify -- it built a session out of `user[:email]` from the
 * request body instead.
 *
 * The library was also the only thing loading the GSI script (via its
 * `useScript` hook). It is NOT in `public/index.html`, so removing the library
 * without loading the script here would leave `window.google` undefined and
 * the button permanently dead.
 */

export const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

/** What `google.accounts.id.initialize`'s callback receives. */
export interface GoogleCredentialResponse {
  /** The raw Google ID token (a JWT). This is the whole point. */
  credential?: string
  select_by?: string
}

export interface GoogleIdConfiguration {
  client_id: string
  callback: (response: GoogleCredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
  context?: string
}

export interface GoogleButtonOptions {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'small' | 'medium' | 'large'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
  locale?: string
}

export interface GoogleIdApi {
  initialize(config: GoogleIdConfiguration): void
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void
  prompt?(listener?: (notification: unknown) => void): void
  cancel?(): void
  disableAutoSelect?(): void
}

/**
 * The OAuth client ID, and nothing else.
 *
 * There is deliberately NO fallback. The previous fallback belonged to
 * Inclusivv's `inclusivv-494922` Google Cloud project, which OOH does not
 * administer, and it took effect silently whenever
 * the env var was missing -- so a misconfigured app looked like it worked while
 * authenticating against someone else's project. The API's allowlist
 * (`GOOGLE_OAUTH_CLIENT_ID`) contains only OOH's own client, so that fallback
 * could now only ever produce a 401. An empty string here is reported to the
 * user instead.
 *
 * `REACT_APP_*` is a compile-time webpack substitution, so this is decided when
 * the app is BUILT, not when it runs. See the PR description.
 */
export const googleClientId = (): string => (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim()

let scriptPromise: Promise<GoogleIdApi> | null = null

const idApi = (): GoogleIdApi | undefined => window.google?.accounts?.id

/**
 * Resolves with `google.accounts.id` once the GSI script is available.
 *
 * A failure rejects rather than hanging: the caller turns that into a visible
 * message. A rejected load is not cached, so a later mount can retry after a
 * transient network failure.
 */
export const loadGoogleIdentity = (): Promise<GoogleIdApi> => {
  const ready = idApi()
  if (ready) return Promise.resolve(ready)
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<GoogleIdApi>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`) as HTMLScriptElement | null
    const script = existing || document.createElement('script')

    script.addEventListener('load', () => {
      const api = idApi()
      if (api) resolve(api)
      else reject(new Error('google.accounts.id is missing after the GSI script loaded'))
    })
    script.addEventListener('error', () => reject(new Error('the GSI script failed to load')))

    if (!existing) {
      script.src = GSI_SCRIPT_SRC
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  })

  scriptPromise.catch(() => {
    scriptPromise = null
  })

  return scriptPromise
}
